import { BookingServiceClient } from 'fitbeat.api-clients';
import { CardioOrResistance, UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import { GymTypes } from 'fitbeat.models';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import BuildIcon from '../../assets/images/build.svg';
import BurnIcon from '../../assets/images/icon-burn-white.svg';
import { getOpenInviteClassList as getOpenInviteClassListAction } from '../../Bookings';
import {
  workoutBookingSliderCardPressed as workoutBookingSliderCardPressedAction,
} from '../../Bookings/actions';
import { isBookingForGroupWorkout } from '../../Bookings/utils/BookingUtils';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import { defaultScale, friendsJoiningText, getInviterUserId } from '../../utils';
import { getUserAvatarFromCache } from '../../utils/avatar-helpers';
import { getGymTimezone } from '../../utils/gyms';
import getMomentFormat from '../../utils/LocaleMoment';
import { Logger } from '../../utils/Logger';
import { horizontalScrollConstants } from './HorizontalScroller';
import { HorizontalScrollerCard } from './HorizontalScrollerCard';
import WorkoutSectionCardWithImageContentWrapper from './WorkoutSectionCardWithImageContentWrapper';
import { workoutSliderCardStyle } from './WorkoutSlider';
import { getAreaIcon } from '../../utils/areaCategoryMapping';

interface IProps {
  onlyOneCardInSlider: boolean;
  lastCardInSlider: boolean;
  workoutBookingSliderCardPressed: () => void;
  userProfile: UserProfile;
  workoutSession: UserWorkoutSession;
  bookingServiceClient: BookingServiceClient;
  openInviteClass: any;
  getOpenInviteClassList: () => void;
  navigation: {
    navigate: (route: string) => void;
  };
  isInvite: boolean;
  t: (key: string) => string;
}

const BookWorkoutSliderCard: FunctionComponent<IProps> = (
  {
    onlyOneCardInSlider,
    lastCardInSlider,
    workoutBookingSliderCardPressed,
    openInviteClass,
    workoutSession,
    isInvite,
    getOpenInviteClassList,
    userProfile,
    t,
  },
) => {

  const [inviterAvatar, setInviterAvatar] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isBookingForGroupWorkout(workoutSession)) {
      getOpenInviteClassList();
    }
  }, []);

  useEffect(() => {
    if (openInviteClass && openInviteClass.openInviteUserProfiles) {
      const inviterId = getInviterUserId(openInviteClass, userProfile.uid);
      if (inviterId) {
        getUserAvatarFromCache(inviterId).then((avatar) => setInviterAvatar(avatar.base64Avatar))
          .catch((e) => Logger.notify(e));
      }
    }
  }, [openInviteClass]);

  const AmyDaveImageHeight = 528;
  const AmyDaveImageWidth = 604;

  const iconSize = defaultScale(25);

  const iconsMap = {
    [CardioOrResistance.Cardio]: <BurnIcon fill={Theme.WHITE} height={iconSize} width={iconSize - defaultScale(9)} />,
    [CardioOrResistance.Resistance]: <BuildIcon stroke={Theme.WHITE} height={iconSize}
      width={iconSize} />,
  };

  const gymTimeZone = getGymTimezone(workoutSession);
  const dayOfBooking = getMomentFormat(workoutSession.startTimeOfFirstExercise, 'ddd', gymTimeZone);
  const bookingTime = getMomentFormat(workoutSession.startTimeOfFirstExercise, 'HH:mm', gymTimeZone);
  const gym = GymResolver.getByName(workoutSession.gymName);
  const isStudioWorkout = gym.type !== GymTypes.Home;

  const friendsJoining = friendsJoiningText(
    openInviteClass, workoutSession!.userProfileId, isStudioWorkout, t);

  const areaCategory = !isStudioWorkout ? 'home' : workoutSession.areaCategory;

  return (
    <HorizontalScrollerCard
      style={workoutSliderCardStyle.container}
      onPress={workoutBookingSliderCardPressed}
      wideCard={onlyOneCardInSlider}
      lastCard={lastCardInSlider}
    >
      {isInvite &&
        <>
          <WorkoutSectionCardWithImageContentWrapper
            imageHeight={inviterAvatar ? horizontalScrollConstants.CARD_HEIGHT : AmyDaveImageHeight}
            imageWidth={inviterAvatar ? horizontalScrollConstants.CARD_HEIGHT : AmyDaveImageWidth}
            header={t('acceptWorkoutInvite')}
            image={inviterAvatar
              ? { uri: `data:image/png;base64,${inviterAvatar}` }
              : require('../../assets/images/workout-cards/pt-amy-and-dave.png')}
            applyHorizontalGradient={!inviterAvatar}
            applyBottomGradient={!inviterAvatar}
            applyImageGradient={!!inviterAvatar}
            alert
          />
          <Text style={styles.bookingText}>
            {dayOfBooking}, {bookingTime}{isStudioWorkout && `, ${gym.displayName}`}
          </Text>
          {!!friendsJoining &&
            <Text style={styles.bookingText}>{friendsJoining}</Text>
          }
        </>
      }

      {!isInvite &&
        <>
          <WorkoutSectionCardWithImageContentWrapper
            imageHeight={AmyDaveImageHeight}
            imageWidth={AmyDaveImageWidth}
            header={t('scheduledWorkout')}
            image={require('../../assets/images/workout-cards/pt-amy-and-dave.png')}
          />
          <View style={styles.textContainer}>
            {areaCategory && getAreaIcon(areaCategory, Theme.WHITE)}
            <Text style={[styles.bookingText, { paddingLeft: defaultScale(5) }]}>
              {dayOfBooking}, {bookingTime}, {gym.displayName}
            </Text>
          </View>
          {!!friendsJoining &&
            <Text style={styles.bookingText}>{friendsJoining}</Text>
          }
        </>
      }

    </HorizontalScrollerCard>
  );
};

const styles = StyleSheet.create({
  bookingText: {
    ...Theme.textStyle.bodyCopyMed,
    color: Theme.WHITE,
    zIndex: 5,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

const mapStateToProps = (state, ownProps: IProps) => {
  const { openInviteClassesByWorkoutId } = state.bookings.bookings;
  return {
    openInviteClass: openInviteClassesByWorkoutId[ownProps.workoutSession.uniqueReference],
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
  const { userProfile, workoutSession, bookingServiceClient } = ownProps;
  return {
    getOpenInviteClassList: () =>
      dispatch(getOpenInviteClassListAction(bookingServiceClient, workoutSession.uniqueReference, true)),
    workoutBookingSliderCardPressed: () => dispatch(
      workoutBookingSliderCardPressedAction(userProfile, workoutSession),
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BookWorkoutSliderCard);
